import { useIntl } from 'react-intl';
import { Box, Link, paletteColors } from '@evaneos/b2b-ui';

import { DiscussionIsResponseRequiredState } from '../../../../resources/discussion/types';
import {
    useMarkAsNoResponseRequiredMutation,
    useMarkAsResponseRequiredMutation,
} from '../../../../resources/discussion/mutations';
import { DossierId } from '../../../../resources/dossier/types';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../../shared/analytics';

type Props = {
    dossierId: DossierId;
    isResponseRequiredState: DiscussionIsResponseRequiredState;
    lastTravellerMessageId: number | null;
    isMessageHovered: boolean;
};

export const ResponseRequiredIndicator = ({
    dossierId,
    isResponseRequiredState,
    lastTravellerMessageId,
    isMessageHovered,
}: Props): JSX.Element | null => {
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);

    const markAsNoResponseRequiredMutation = useMarkAsNoResponseRequiredMutation(dossierId);
    const markAsResponseRequiredMutation = useMarkAsResponseRequiredMutation(dossierId);

    const handleMarkAsNoResponseRequired = () => {
        trackEvent((ampli, defaultProperties) => {
            ampli.rmDiscussionResponseNoRequiredSubmitted({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });
        markAsNoResponseRequiredMutation.mutate({
            messageId: lastTravellerMessageId,
            reason: 'no_response_required',
        });
    };

    const handleMarkAsResponseRequired = () => {
        markAsResponseRequiredMutation.mutate();
    };

    if (isResponseRequiredState === 'AUTOMATICALLY_MARKED_AS_NO_RESPONSE_REQUIRED') {
        return null;
    }

    const isMarkedAsNoResponseRequired =
        isResponseRequiredState === 'MANUALLY_MARKED_AS_NO_RESPONSE_REQUIRED';

    const hoveredContent = isMarkedAsNoResponseRequired ? (
        <Box component="span" display="flex" gap={0.5}>
            {formatMessage({ id: 'discussion.marked_no_response_required' })}
            {' - '}
            <Link
                component="button"
                variant="caption"
                color="text.action"
                sx={{
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    lineHeight: '1.6rem',
                    '&:hover': {
                        color: paletteColors.green[600],
                        textDecoration: 'none',
                    },
                }}
                onClick={handleMarkAsResponseRequired}
                data-testid="response-required-button"
            >
                {formatMessage({ id: 'form.cancel' })}
            </Link>
        </Box>
    ) : (
        <Box component="span" display="flex" gap={0.5}>
            {formatMessage({ id: 'discussion.no_response_required_question' })}{' '}
            <Link
                component="button"
                variant="caption"
                color="text.action"
                sx={{
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    lineHeight: '1.6rem',
                    '&:hover': {
                        color: paletteColors.green[600],
                        textDecoration: 'none',
                    },
                }}
                onClick={handleMarkAsNoResponseRequired}
                data-testid="no-response-required-button"
            >
                {formatMessage({
                    id: 'discussion.mark_as_no_response_required_button',
                })}
            </Link>
        </Box>
    );

    const staticContent = isMarkedAsNoResponseRequired ? (
        <Box component="span" marginLeft={0.5}>
            {formatMessage({ id: 'discussion.no_response_required_label' })}
        </Box>
    ) : null;

    return <Box component="span">{isMessageHovered ? hoveredContent : staticContent}</Box>;
};
